import { useEffect, useMemo, useState } from 'react';
import { where, type QueryConstraint } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCollection } from './useCollection';
import { hasCouncilFullRead } from '../lib/visibility';
import { tenantWhere, useActiveTenantId } from '../lib/tenant';
import { functions } from '../firebase';
import { createRefreshController, toError } from '../lib/silentRefresh';
import {
  COMMS_ALL_COUNCIL,
  type Communication,
  type CommunicationTypeId,
  type CommunicationTypeOption,
  type CommScope,
  type UserProfile,
} from '../types';

export interface CommunicationsAuthorization {
  canSend: boolean;
  tenantWide: boolean;
  districtIds: string[];
  policyRevisionId: string;
  policyFingerprint: string;
  membershipFingerprint: string;
  communicationTypes: CommunicationTypeOption[];
}

export interface CommunicationPreview {
  groups: Array<{ label: string; people: Array<{ name: string; role: string }> }>;
  audienceLabel: string;
  description: string;
  count: number;
  communicationType: CommunicationTypeOption;
}

export function useCommunicationsAuthorization(profile: UserProfile) {
  const activeTenantId = useActiveTenantId();
  // `refreshError` is a failed background refresh: the data above it is still the last good answer.
  const [state, setState] = useState<{
    data: CommunicationsAuthorization | null;
    loading: boolean;
    error: Error | null;
    refreshError: Error | null;
  }>({ data: null, loading: true, error: null, refreshError: null });
  const identityKey = `${activeTenantId}|${profile.id}|${(profile.positionIds ?? []).join(',')}|${profile.role}`;

  useEffect(() => {
    // Clear-first only when the identity/council context changes (this effect re-runs); focus refreshes silently.
    const controller = createRefreshController({
      fetch: () => httpsCallable<Record<string, never>, CommunicationsAuthorization>(functions, 'getCommunicationsAuthorizationV2')({}).then((r) => r.data),
      onStart: () => setState({ data: null, loading: true, error: null, refreshError: null }),
      onResult: (data) => setState({ data, loading: false, error: null, refreshError: null }),
      onError: (error, background) => {
        const err = toError(error, 'Communications authorization could not be loaded.');
        if (background) {
          console.warn('Background refresh of Communications authorization failed; keeping the data on screen.', err);
          setState((s) => ({ ...s, refreshError: err }));
        } else {
          setState({ data: null, loading: false, error: err, refreshError: null });
        }
      },
    });
    const onFocus = () => controller.refresh('focus');
    controller.start();
    window.addEventListener('focus', onFocus);
    return () => {
      controller.dispose();
      window.removeEventListener('focus', onFocus);
    };
  }, [identityKey]);
  return state;
}

export function useCommunicationPreview(
  districtId: string,
  scope: CommScope | null,
  communicationTypeId: CommunicationTypeId | '',
  enabled: boolean,
) {
  const [state, setState] = useState<{
    data: CommunicationPreview | null;
    loading: boolean;
    error: Error | null;
  }>({ data: null, loading: enabled, error: null });

  useEffect(() => {
    if (!enabled || !districtId || !communicationTypeId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    const call = httpsCallable<{
      districtId: string;
      scope: CommScope | null;
      communicationTypeId: CommunicationTypeId;
    }, CommunicationPreview>(
      functions,
      'previewCommunicationV2',
    );
    void call({ districtId, scope, communicationTypeId }).then(
      (result) => !cancelled && setState({ data: result.data, loading: false, error: null }),
      (error: unknown) => !cancelled && setState({
        data: null,
        loading: false,
        error: error instanceof Error ? error : new Error('Recipient preview could not be loaded.'),
      }),
    );
    return () => { cancelled = true; };
  }, [districtId, scope, communicationTypeId, enabled]);
  return state;
}

/** Communication metadata this user may read: admins (and council-wide
 *  members) see all; everyone else sees sends to their own district plus
 *  council-wide sends. Sorted newest first client-side (avoids a composite
 *  index alongside the `in` filter). */
export function useVisibleCommunications(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const constraints: QueryConstraint[] = useMemo(
    () => [
      tenantWhere(),
      ...(unscoped || !profile.districtId ? [] : [where('districtId', 'in', [profile.districtId, COMMS_ALL_COUNCIL])]),
    ],
    [unscoped, profile.districtId],
  );
  const enabled = unscoped || !!profile.districtId;
  const q = useCollection<Communication>('communications', constraints, enabled);
  const data = useMemo(
    () =>
      [...q.data].sort((a, b) => (b.sentAt?.toMillis?.() ?? 0) - (a.sentAt?.toMillis?.() ?? 0)),
    [q.data],
  );
  return { ...q, data };
}
